import React from "react"
import { Alert, Box } from "@carrotfertility/carotene-core"
import { Skeleton } from "@internal/ui"
import { PhotoIdUpload } from "../../intake/PhotoIdUpload"
import { usePhotoIdOnFile, useUploadPhotoId } from "../../intake/photoIdHooks"
import { useBookingTracking } from "../tracking"

// Mounted only after booking succeeds, when the member has a linked Healthie patient.
export function PhotoIdPage({
  appointmentId,
  onContinue
}: {
  appointmentId: string
  onContinue: () => void
}): JSX.Element {
  // Recheck Healthie rather than relying on cached status, which may have been
  // fetched before booking or before an ID was uploaded elsewhere.
  const status = usePhotoIdOnFile({ enabled: true, refreshOnMount: true })
  const tracking = useBookingTracking()
  const upload = useUploadPhotoId(() => tracking.onEvent({ type: "photo_id_upload_completed", appointmentId }))
  // A failed status check must not block confirmation. Upload failures stay on this
  // screen for retry, matching the appointment's Complete forms path.
  const shouldProceedToBookedPage =
    status.isError || upload.isSuccess || (!status.isFetching && status.isSuccess && status.data.onFile)

  React.useEffect(() => {
    if (shouldProceedToBookedPage) onContinue()
  }, [shouldProceedToBookedPage, onContinue])

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(theme.tokens.spacing.lg),
        maxInlineSize: 640,
        marginInline: "auto"
      })}
    >
      {status.isFetching || status.isLoading || shouldProceedToBookedPage ? (
        <Box role="status" aria-label="Checking photo ID" aria-busy="true">
          <Skeleton width="100%" height={240} />
        </Box>
      ) : (
        <>
          <PhotoIdUpload
            onImageAdded={(side) => tracking.onEvent({ type: "photo_id_image_added", appointmentId, side })}
            onSubmit={(images) => upload.mutate(images)}
            submitting={upload.isPending}
          />
          {upload.isError && (
            <Alert severity="error">
              ID upload failed: {upload.error instanceof Error ? upload.error.message : "unknown error"}
            </Alert>
          )}
        </>
      )}
    </Box>
  )
}
